import React, { useState, useEffect } from "react";
import {
  Paper,
  Typography,
  Box,
  CircularProgress,
  Chip,
  Button,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import StarIcon from "@material-ui/icons/Star";
import RateReviewIcon from "@material-ui/icons/RateReview";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";

import api from "../../services/api";
import NpsReportModal from "../../components/NpsReportModal";

const useStyles = makeStyles((theme) => ({
  rootPaper: {
    padding: theme.spacing(2.5),
    borderRadius: 14,
    border: "1px solid #e2e8f0",
    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.03)",
    backgroundColor: "#ffffff",
    marginBottom: theme.spacing(3),
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
    gap: 8,
  },
  title: {
    fontWeight: 700,
    fontSize: "1.15rem",
    display: "flex",
    alignItems: "center",
    gap: 10,
    color: "#0f172a",
  },
  scoreBanner: {
    padding: "16px 20px",
    borderRadius: 12,
    background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
    color: "#ffffff",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 16,
    marginBottom: theme.spacing(2.5),
  },
  cardItem: {
    padding: "14px 16px",
    borderRadius: 10,
    border: "1px solid #e2e8f0",
    backgroundColor: "#f8fafc",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    height: "100%",
  },
}));

const NpsQuickOverviewDashboard = ({ whatsappId = "", startDate = "", endDate = "" }) => {
  const classes = useStyles();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const fetchNps = async () => {
    setLoading(true);
    try {
      let days = undefined;
      if (startDate && endDate) {
        const start = new Date(startDate);
        const end = new Date(endDate);
        const diffTime = Math.abs(end - start);
        days = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) || 30;
      }

      const { data: res } = await api.get("/whatsapp/nps-report", {
        params: {
          whatsappId: whatsappId || undefined,
          days: days || 30,
        },
      });
      setData(res);
    } catch (err) {
      console.error("Erro ao carregar resumo NPS:", err);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchNps();
  }, [whatsappId, startDate, endDate]);

  const getScoreColor = (score) => {
    if (score >= 4.5) return "#10b981"; // emerald
    if (score >= 3.8) return "#0284c7"; // blue
    if (score >= 3.0) return "#f59e0b"; // amber
    return "#ef4444"; // red
  };

  const overall = data?.overall || { totalEvaluations: 0, avgRating: 0 };
  const byConnection = data?.byConnection || [];
  const byUser = data?.byUser || [];

  return (
    <>
      <Paper className={classes.rootPaper} variant="outlined">
        {/* HEADER */}
        <div className={classes.header}>
          <div className={classes.title}>
            <RateReviewIcon style={{ color: "#f59e0b", fontSize: "1.45rem" }} />
            <span>Satisfação do Cliente (NPS)</span>
            <Chip
              size="small"
              label={
                overall.totalEvaluations > 0
                  ? `${overall.totalEvaluations} avaliações no período`
                  : "Sem avaliações no período"
              }
              style={{
                backgroundColor: overall.totalEvaluations > 0 ? "#fef3c7" : "#f1f5f9",
                color: overall.totalEvaluations > 0 ? "#92400e" : "#64748b",
                fontWeight: 700,
                fontSize: "0.75rem",
              }}
            />
          </div>

          <Button
            size="small"
            variant="outlined"
            style={{
              borderColor: "#f59e0b",
              color: "#b45309",
              fontWeight: 700,
              textTransform: "none",
              borderRadius: 8,
              padding: "4px 12px",
            }}
            endIcon={<OpenInNewIcon style={{ fontSize: 16 }} />}
            onClick={() => setModalOpen(true)}
          >
            Abrir Relatório Completo
          </Button>
        </div>

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={4} style={{ gap: 8 }}>
            <CircularProgress size={22} style={{ color: "#f59e0b" }} />
            <Typography variant="body2" style={{ color: "#64748b" }}>
              Carregando notas e índices de satisfação...
            </Typography>
          </Box>
        ) : (
          <div>
            {/* SCORE HERO BANNER */}
            <div className={classes.scoreBanner}>
              <div>
                <Typography style={{ fontSize: "0.75rem", color: "#94a3b8", fontWeight: 700, textTransform: "uppercase" }}>
                  Média de Satisfação Geral
                </Typography>
                <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginTop: 4 }}>
                  <Typography style={{ fontSize: "2.4rem", fontWeight: 900, lineHeight: 1, color: getScoreColor(overall.avgRating) }}>
                    {overall.totalEvaluations > 0 ? overall.avgRating.toFixed(2) : "—"}
                  </Typography>
                  <Typography style={{ fontSize: "1.1rem", color: "#64748b", fontWeight: 700 }}>
                    / 5.0 ⭐
                  </Typography>
                </div>
                <Typography style={{ fontSize: "0.75rem", color: "#cbd5e1", marginTop: 4 }}>
                  {overall.totalEvaluations > 0
                    ? `${overall.totalEvaluations} clientes responderam à pesquisa de avaliação`
                    : "Nenhuma avaliação registrada com os filtros aplicados"}
                </Typography>
              </div>

              {/* STATS RÁPIDOS (5 ESTRELAS E % APROVAÇÃO) */}
              {overall.totalEvaluations > 0 && (
                <div style={{ display: "flex", gap: 20 }}>
                  <div style={{ textAlign: "right" }}>
                    <Typography style={{ fontSize: "0.72rem", color: "#94a3b8", textTransform: "uppercase", fontWeight: 600 }}>
                      Nota Máxima (5 ⭐)
                    </Typography>
                    <Typography style={{ fontSize: "1.4rem", fontWeight: 800, color: "#10b981" }}>
                      {overall.breakdown ? overall.breakdown[5] || 0 : 0}
                    </Typography>
                    <Typography style={{ fontSize: "0.7rem", color: "#cbd5e1" }}>
                      {Math.round(((overall.breakdown?.[5] || 0) / overall.totalEvaluations) * 100)}% do total
                    </Typography>
                  </div>

                  <div style={{ textAlign: "right" }}>
                    <Typography style={{ fontSize: "0.72rem", color: "#94a3b8", textTransform: "uppercase", fontWeight: 600 }}>
                      Aprovação (4 e 5 ⭐)
                    </Typography>
                    <Typography style={{ fontSize: "1.4rem", fontWeight: 800, color: "#38bdf8" }}>
                      {Math.round(
                        (((overall.breakdown?.[5] || 0) + (overall.breakdown?.[4] || 0)) / overall.totalEvaluations) * 100
                      )}%
                    </Typography>
                    <Typography style={{ fontSize: "0.7rem", color: "#cbd5e1" }}>
                      satisfação positiva
                    </Typography>
                  </div>
                </div>
              )}
            </div>

            {/* DUAS TABELAS LADO A LADO: NOTA POR CONEXÃO & NOTA POR ATENDENTE */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 16 }}>
              {/* TABELA CONEXÕES */}
              <Paper variant="outlined" style={{ borderRadius: 10, overflow: "hidden" }}>
                <div style={{ padding: "10px 14px", backgroundColor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                  <Typography variant="subtitle2" style={{ fontWeight: 700, color: "#1e293b", fontSize: "0.85rem" }}>
                    🌐 Nota Média por Conexão / Marca
                  </Typography>
                </div>
                <Table size="small">
                  <TableHead style={{ backgroundColor: "#f1f5f9" }}>
                    <TableRow>
                      <TableCell style={{ fontWeight: 700, fontSize: "0.75rem" }}>Conexão</TableCell>
                      <TableCell align="center" style={{ fontWeight: 700, fontSize: "0.75rem" }}>Respostas</TableCell>
                      <TableCell align="right" style={{ fontWeight: 700, fontSize: "0.75rem" }}>Média</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {byConnection.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} align="center" style={{ color: "#94a3b8", fontSize: "0.75rem", padding: 14 }}>
                          Sem avaliações nesta conexão.
                        </TableCell>
                      </TableRow>
                    ) : (
                      byConnection.map((conn) => (
                        <TableRow key={conn.whatsappId} hover>
                          <TableCell style={{ fontWeight: 600, fontSize: "0.8rem", color: "#1e293b" }}>
                            <span
                              style={{
                                display: "inline-block",
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                backgroundColor: conn.whatsappColor || "#3b82f6",
                                marginRight: 6,
                              }}
                            />
                            {conn.whatsappName}
                          </TableCell>
                          <TableCell align="center" style={{ fontSize: "0.78rem", color: "#64748b" }}>
                            {conn.totalEvaluations}
                          </TableCell>
                          <TableCell align="right">
                            <span
                              style={{
                                fontWeight: 800,
                                fontSize: "0.85rem",
                                color: getScoreColor(conn.avgRating),
                              }}
                            >
                              ⭐ {conn.avgRating.toFixed(2)}
                            </span>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </Paper>

              {/* TABELA ATENDENTES */}
              <Paper variant="outlined" style={{ borderRadius: 10, overflow: "hidden" }}>
                <div style={{ padding: "10px 14px", backgroundColor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                  <Typography variant="subtitle2" style={{ fontWeight: 700, color: "#1e293b", fontSize: "0.85rem" }}>
                    👩‍💼 Nota Média por Atendente (Humano e IA)
                  </Typography>
                </div>
                <Table size="small">
                  <TableHead style={{ backgroundColor: "#f1f5f9" }}>
                    <TableRow>
                      <TableCell style={{ fontWeight: 700, fontSize: "0.75rem" }}>Atendente</TableCell>
                      <TableCell align="center" style={{ fontWeight: 700, fontSize: "0.75rem" }}>Respostas</TableCell>
                      <TableCell align="right" style={{ fontWeight: 700, fontSize: "0.75rem" }}>Média</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {byUser.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} align="center" style={{ color: "#94a3b8", fontSize: "0.75rem", padding: 14 }}>
                          Sem atendimentos avaliados.
                        </TableCell>
                      </TableRow>
                    ) : (
                      byUser.map((u) => (
                        <TableRow key={String(u.userId)} hover>
                          <TableCell style={{ fontWeight: 600, fontSize: "0.8rem", color: "#1e293b" }}>
                            {u.userId === null ? "🤖 Assistente (IA)" : `👤 ${u.userName}`}
                          </TableCell>
                          <TableCell align="center" style={{ fontSize: "0.78rem", color: "#64748b" }}>
                            {u.totalEvaluations}
                          </TableCell>
                          <TableCell align="right">
                            <span
                              style={{
                                fontWeight: 800,
                                fontSize: "0.85rem",
                                color: getScoreColor(u.avgRating),
                              }}
                            >
                              ⭐ {u.avgRating.toFixed(2)}
                            </span>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </Paper>
            </div>
          </div>
        )}
      </Paper>

      {/* MODAL DETALHADO */}
      {modalOpen && (
        <NpsReportModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          defaultWhatsappId={whatsappId || "all"}
        />
      )}
    </>
  );
};

export default NpsQuickOverviewDashboard;
